/* eslint-disable max-lines */
// deps
import React, {
    ReactElement,
    memo,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useEffect,
    useState,
    useRef,
} from "react";

// internal deps
import { withStyles } from "@swiggy-private/core-ui";
import {
    GroupOrderCartContext,
    useOnBack,
    AnalyticsContext,
    useSessionStorage,
    isJioHotstarFlow,
} from "@swiggy-private/checkout-core";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CartSavingsBannerOneLineBlack, CartSavingsBannerOneLineV2 } from "@swiggy-private/super-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import styles from "./Header.scss";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// helpers
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { getSavingsBannerData } from "@cart-client/helpers/Cart";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import { isBlackMembershipPlan } from "@cart-client/helpers/CheckBlackMembershipPlan";
import { getAddressNudgeTooltipData } from "@cart-client/helpers/CartAddress";
import { isCartHeaderTypeLottie } from "@cart-client/helpers/cartHeaderLottie";

// style components
import { HeaderContainer, HeaderExtensionWrapper } from "./Header.style";

// hooks
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";

// components
import HeaderContent from "./components/HeaderContent";
import OtgLottie from "./components/OtgLottie";
import BoltStampLottie from "./components/BoltStampLottieContainer";
import { getBoltSlaData } from "@cart-client/helpers/Bolt";
import AddressNudgeToolTip from "./components/AddressNudgeToolTip";

//hooks
import { useCartAddressNudgeAnalytics } from "@cart-client/hooks/useCartAddressNudgeAnalytics";

//constants and types
import { ADDRESS_NUDGE_TOOLTIP_STATUS } from "@swiggy-private/cart-types";
import { OtgLottieDelay } from "./components/constants";
import { ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY } from "@cart-client/constants/CartAddress";
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import HeaderStampLottie from "./components/HeaderStampLottie";
import LottieSection from "./components/LottieSection";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import { LottieType } from "./components/helper";
import { clickMenuBack } from "@cart-client/analytics/events";

interface CommonHeaderProps {
    children: ReactNode;
    onBack?: VoidFunction;
    containerClass?: string;
    isVisible: boolean;
    isPageScrolled?: boolean;
    showAddressWidgetTop?: boolean;
    isEmptyCart: boolean;
}

export const ACCESSIBILITY_TEXTS = {
    GO_BACK: "Go Back",
};

function CommonHeader(props: CommonHeaderProps): ReactElement {
    const { isEmptyCart, onBack: propsOnBack } = props;
    const { cartState } = useContext(CartContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { data } = useContext(CartFlowContext);
    const { state: groupCartData } = useContext(GroupOrderCartContext);
    const { updateCartInProgress, showCouponModal } = useContext(UpdateCartContext);
    const { cartHeaderState } = useContext(CartHeaderContext);
    const { cartHeaderLottieState } = useContext(CartHeaderLottieContext);
    const {
        cartAddressState: { addressNudgeConfig },
        setCartAddressState,
    } = useContext(CartAddressContext);

    const { reviewedCart: cartResponse } = cartState;
    const savingsBannerData = getSavingsBannerData(cartResponse);

    const isUserBlackMember = useMemo(() => isBlackMembershipPlan(cartResponse), [cartResponse]);


    const [showOtgLottie, setShowOtgLottie] = useState(false);
    const [showExpressLottie, setShowExpressLottie] = useState(false);

    //states to track if lottie animations have shown
    const [otgLottieShown, setOtgLottieShown] = useState<boolean>(false);
    const [boltOrExpressLottieShown, setBoltOrExpressLottieShown] = useState<boolean>(false);
    const [headerLottieShown, setHeaderLottieShown] = useState<boolean>(false);
    const [headerStampLottieShown, setHeaderStampLottieShown] = useState<boolean>(false);

    //flag to determine addressNudgeTooltip display status
    const { addressNudgeTooltipStatus } = addressNudgeConfig;
    const isGiftingCartFlow = isGiftingCartSource(data?.cartSource);

    //determine how long to show addressNudgeTooltip and get tooltip text
    const { addressNudgeToolTipDuration, addressNudgeToolTipText } = useMemo(
        () => getAddressNudgeTooltipData(addressNudgeConfig),
        [addressNudgeConfig],
    );

    const { isBlack, isOtg, isExpress, showFinalState, themeChange, startAnimation, hideTheme, isBolt } =
        cartHeaderState;

    const {
        cartType,
        lottieArray,
        themeChange: themeChangeLottie,
        startAnimation: startAnimationLottie,
        showFinalState: showFinalStateLottie,
        hideTheme: hideThemeLottie,
    } = cartHeaderLottieState;

    const is99StoreLottie = cartType === LottieCartType._99STORE;
    const isEOCartLottie = cartType === LottieCartType.EO_CART;
    const isCartHeaderTypeLottieExists = isCartHeaderTypeLottie(cartType);
    const hasStampLottie = lottieArray.some((lottie) => lottie.type === LottieType.STAMP);
    const show99StoreStampLottie = hasStampLottie && (startAnimationLottie || showFinalStateLottie);

    const shouldShowOtgLottieDirectly = isBlack && isOtg && showFinalState;
    const shouldDelayOtgLottie = isBlack && isOtg && themeChange && showExpressLottie;
    const showNonBoltOtgLottie = !isBolt && showOtgLottie && isOtg;
    const { boltSla, isRangeSla } = useMemo(
        () => getBoltSlaData(isBolt, cartResponse?.data?.addresses?.[0]),
        [cartResponse?.data?.addresses, isBolt],
    );
    //flag to track if all lottie animations have finished
    const lottieAnimationsShown = useMemo(() => {
        if (isCartHeaderTypeLottieExists) {
            return hasStampLottie ? headerStampLottieShown : headerLottieShown;
        }

        //if only otg lottie is playing or otg lottie is finishing last
        if (shouldDelayOtgLottie || (showOtgLottie && !showExpressLottie)) {
            return otgLottieShown;
        }

        //if only express lottie is playing
        if (!showOtgLottie && showExpressLottie) {
            return boltOrExpressLottieShown;
        }

        return boltOrExpressLottieShown && otgLottieShown;
    }, [
        boltOrExpressLottieShown,
        otgLottieShown,
        shouldDelayOtgLottie,
        showExpressLottie,
        showOtgLottie,
        hasStampLottie,
        isCartHeaderTypeLottieExists,
        headerStampLottieShown,
        headerLottieShown,
    ]);

    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

    const nudgeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [, setIsNudgeTooltipShownFlag] = useSessionStorage(ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY, false);

    const hideAddressNudgeTooltip = useCallback(() => {
        setCartAddressState((prevCartAddressState) => ({
            ...prevCartAddressState,
            addressNudgeConfig: {
                ...prevCartAddressState.addressNudgeConfig,
                addressNudgeTooltipStatus: ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN,
            },
        }));
    }, [setCartAddressState]);

    const handleCloseAddressNudgeToolTip = useCallback(() => {
        if (nudgeTimeoutRef?.current) {
            clearTimeout(nudgeTimeoutRef.current);
        }
        hideAddressNudgeTooltip();
    }, [hideAddressNudgeTooltip]);

    useEffect(() => {
        if (hideThemeLottie || showFinalStateLottie) {
            setHeaderStampLottieShown(true);
            setHeaderLottieShown(true);
        }
    }, [hideThemeLottie, showFinalStateLottie]);

    useEffect(() => {
        const clearExistingTimeout = () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
        };

        //if hideTheme is true which means lottie animations will not play or if showFinalState is true then direclty render nudge tooltip
        if (hideTheme || showFinalState) {
            setOtgLottieShown(true);
            setBoltOrExpressLottieShown(true);
        }

        if (!isBlack || !isOtg) {
            setShowOtgLottie(false);
            clearExistingTimeout();
        }

        if (shouldShowOtgLottieDirectly) {
            setShowOtgLottie(true);
            setShowExpressLottie(false);
            clearExistingTimeout();
            return;
        } else if (shouldDelayOtgLottie) {
            timeoutIdRef.current = setTimeout(
                () => {
                    setShowOtgLottie(true);
                    timeoutIdRef.current = null; // Clear the ref after timeout executes
                },
                isExpress ? OtgLottieDelay.isExpress : OtgLottieDelay.isNonExpress,
            );
            return;
        }

        return clearExistingTimeout;
    }, [isBlack, isExpress, isOtg, shouldDelayOtgLottie, shouldShowOtgLottieDirectly, hideTheme, showFinalState]);

    useEffect(() => {
        if (startAnimation) {
            setShowExpressLottie(true);
        }
    }, [startAnimation]);

    useEffect(() => {
        //once we can show address nudge tooltip
        if (lottieAnimationsShown && addressNudgeTooltipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW) {
            //set key in session storage that nudge tooltip has shown
            setIsNudgeTooltipShownFlag(true);
            //set cart address state to show address nudge icon
            setCartAddressState((prevCartAddressState) => ({
                ...prevCartAddressState,
                addressNudgeConfig: {
                    ...prevCartAddressState.addressNudgeConfig,
                    showAddressNudgeIcon: true,
                },
            }));
            //set timeout to close address nudge tooltip after nudgeConfigTimout
            nudgeTimeoutRef.current = setTimeout(() => {
                hideAddressNudgeTooltip();
            }, addressNudgeToolTipDuration);
        }

        return () => {
            if (nudgeTimeoutRef?.current) {
                clearTimeout(nudgeTimeoutRef.current);
            }
        };
    }, [
        addressNudgeToolTipDuration,
        addressNudgeTooltipStatus,
        hideAddressNudgeTooltip,
        lottieAnimationsShown,
        setCartAddressState,
        setIsNudgeTooltipShownFlag,
    ]);

    const onBack = useOnBack();

    const onBackButtonClick = useCallback(() => {
        updateStatusBarCleanup();
        clickMenuBack(clickEvent, cartState?.localCart?.cart_id?.toString(), groupCartData?.data?.group_cart_id, {
            isEmptyCart,
        });
        if (typeof propsOnBack === "function") {
            propsOnBack();
            return;
        }
        onBack();
    }, [
        cartState?.localCart?.cart_id,
        clickEvent,
        groupCartData?.data?.group_cart_id,
        onBack,
        propsOnBack,
        isEmptyCart,
    ]);

    const { onImpressionSavingsOneLine } = useCartRendererAnalytics(cartResponse);

    const headerExtensionElement = useMemo(() => {
        if (themeChange || showFinalState || showFinalStateLottie || themeChangeLottie) {
            return (
                <HeaderExtensionWrapper hasStamp={isBolt || isOtg || hasStampLottie} isBlack={isBlack}>
                    <CartSavingsBannerOneLineBlack
                        title={savingsBannerData?.title || ""}
                        subtitle={savingsBannerData?.subtitle || ""}
                        details={savingsBannerData?.message_details || {}}
                        pauseUpdate={showCouponModal || updateCartInProgress}
                        onImpression={onImpressionSavingsOneLine}
                        onUpdate={onImpressionSavingsOneLine}
                        isExpress={isExpress}
                        isOTG={isOtg}
                        showFinalTheme={showFinalState || showFinalStateLottie}
                        isBolt={isBolt}
                        is99Store={is99StoreLottie}
                        isEOCart={isEOCartLottie}
                        savingsBannerGradient={cartHeaderLottieState?.themeConfig?.savings_banner_gradient}
                        savingsShimmerGradient={cartHeaderLottieState?.themeConfig?.savings_shimmer_gradient}
                    />
                </HeaderExtensionWrapper>
            );
        }

        if (isEmpty(savingsBannerData)) {
            return null;
        }
        return (
            <HeaderExtensionWrapper>
                <CartSavingsBannerOneLineV2
                    isCartV2
                    title={savingsBannerData?.title || ""}
                    subtitle={savingsBannerData?.subtitle || ""}
                    details={savingsBannerData?.message_details || {}}
                    pauseUpdate={showCouponModal || updateCartInProgress}
                    onImpression={onImpressionSavingsOneLine}
                    onUpdate={onImpressionSavingsOneLine}
                    isGiftingCartFlow={isGiftingCartFlow}
                />
            </HeaderExtensionWrapper>
        );
    }, [
        themeChange,
        showFinalState,
        showFinalStateLottie,
        themeChangeLottie,
        savingsBannerData,
        showCouponModal,
        updateCartInProgress,
        onImpressionSavingsOneLine,
        isGiftingCartFlow,
        isBolt,
        isOtg,
        hasStampLottie,
        isBlack,
        isExpress,
        is99StoreLottie,
        isEOCartLottie,
        cartHeaderLottieState?.themeConfig?.savings_banner_gradient,
        cartHeaderLottieState?.themeConfig?.savings_shimmer_gradient
    ]);

    const { onImpressionCheckoutLatLng } = useCartAddressNudgeAnalytics();

    useEffect(() => {
        onImpressionCheckoutLatLng();
    }, [onImpressionCheckoutLatLng]);

    return (
        <>
            <HeaderContainer
                isLottieAnimation={
                    cartHeaderState || cartHeaderLottieState
                        ? cartHeaderState.startAnimation ||
                          cartHeaderState.showFinalState ||
                          cartHeaderLottieState.startAnimation ||
                          cartHeaderLottieState.showFinalState
                        : false
                }
                data-testid={COMMON_HEADER_TEST_IDS.CART_HEADER}
            >
                <LottieSection
                    cartType={cartType}
                    lottieArray={lottieArray}
                    isCartHeaderTypeLottie={isCartHeaderTypeLottieExists}
                    isOtg={isOtg}
                    isBolt={isBolt}
                    isExpress={isExpress}
                    isBlack={isBlack}
                    startAnimation={startAnimation || startAnimationLottie}
                    showFinalState={showFinalState || showFinalStateLottie}
                    boltSla={boltSla}
                    isRangeSla={isRangeSla}
                    setBoltOrExpressLottieShown={setBoltOrExpressLottieShown}
                    setHeaderLottieShown={setHeaderLottieShown}
                />

                <HeaderContent
                    isUserBlackMember={isUserBlackMember}
                    onBackButtonClick={onBackButtonClick}
                    isGiftingCartFlow={isGiftingCartFlow}
                    showAddressWidgetTop={props.showAddressWidgetTop}
                    isJioHotstarFlow={isJioHotstarFlow()}
                >
                    {props.children}
                </HeaderContent>
                {headerExtensionElement && <>{headerExtensionElement}</>}

                {themeChangeLottie && (
                    <HeaderStampLottie
                        setHeaderStampLottieShown={setHeaderStampLottieShown}
                        showFinalState={showFinalStateLottie}
                        showStampLottie={show99StoreStampLottie}
                        lottieArray={lottieArray}
                        cartType={cartType}
                    />
                )}

                {showNonBoltOtgLottie && !isCartHeaderTypeLottieExists && (
                    <OtgLottie
                        showOtgLottie={showOtgLottie}
                        showFinalState={showFinalState}
                        setOtgLottieShown={setOtgLottieShown}
                    />
                )}
                {isBolt && themeChange && boltSla && !isCartHeaderTypeLottieExists && (
                    <BoltStampLottie
                        isBlack={isBlack}
                        sla={boltSla}
                        isOtg={isOtg}
                        showFinalState={showFinalState}
                        showBoltLottie={startAnimation || showFinalState}
                        isRangeSla={isRangeSla}
                        setOtgLottieShown={setOtgLottieShown}
                    />
                )}
            </HeaderContainer>
            {lottieAnimationsShown && !isJioHotstarFlow() && (
                <AddressNudgeToolTip
                    addressNudgeToolTipText={addressNudgeToolTipText}
                    addressNudgeToolTipStatus={addressNudgeTooltipStatus}
                    onCloseClick={handleCloseAddressNudgeToolTip}
                    isUserBlackMember={isUserBlackMember || themeChange}
                />
            )}
        </>
    );
}

export default memo(withStyles(CommonHeader, styles));
